/**
 * Shared UI utility functions
 * Centralized location for common UI operations to avoid duplication
 */

/**
 * Show a toast notification message
 * Creates a single toast element if it doesn't exist and displays the message
 */
export function showToast(message: string, isError: boolean = false, duration: number = 2500): void {
    let toast = document.getElementById("modern-toast");
    if (!toast) {
        toast = document.createElement("div");
        toast.id = "modern-toast";
        toast.className = "modern-toast";
        document.body.appendChild(toast);
    }
    // Clear any previous auto-dismiss timer
    const prevTimer = (toast as any).__toastTimer;
    if (prevTimer) clearTimeout(prevTimer);

    if (isError) {
        toast.innerHTML = `<span>${message}</span><button class="toast-close-btn" aria-label="Dismiss" style="margin-left:0.75rem;background:none;border:none;color:inherit;cursor:pointer;font-size:1rem;line-height:1;">&times;</button>`;
        const closeBtn = toast.querySelector('.toast-close-btn') as HTMLButtonElement | null;
        if (closeBtn) closeBtn.addEventListener('click', () => toast!.classList.remove('show'));
    } else {
        toast.textContent = message;
        (toast as any).__toastTimer = setTimeout(() => toast?.classList.remove("show"), duration);
    }
    toast.classList.add("show");
}

/**
 * Set button loading state
 */
export function setButtonLoading(button: HTMLButtonElement | null, isLoading: boolean): void {
    if (!button) return;
    
    button.disabled = isLoading;
    
    if (isLoading) {
        button.classList.add("loading");
        button.style.opacity = "0.5";
        button.style.cursor = "not-allowed";
        
        const spinner = button.querySelector('.spinner') as HTMLElement;
        const label = button.querySelector('.login-label, .btn-label') as HTMLElement;
        if (spinner) spinner.style.display = 'inline-block';
        if (label) label.style.opacity = '0.7';
    } else {
        button.classList.remove("loading");
        button.style.opacity = "1";
        button.style.cursor = "pointer";
        
        const spinner = button.querySelector('.spinner') as HTMLElement;
        const label = button.querySelector('.login-label, .btn-label') as HTMLElement;
        if (spinner) spinner.style.display = 'none';
        if (label) label.style.opacity = '1';
    }
}

/**
 * Escape HTML entities in a string to prevent XSS attacks
 * Escapes special HTML characters that could be used for XSS
 */
export function escapeHTML(text: string): string {
    const map: {[key: string]: string} = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#x27;',
        '/': '&#x2F;'
    };
    return text.replace(/[&<>"'\/]/g, char => map[char]);
}

/**
 * Remove all event listeners from an element by cloning it
 */
export function removeAllEventListeners(element: HTMLElement): HTMLElement {
    const newElement = element.cloneNode(true) as HTMLElement;
    element.parentNode?.replaceChild(newElement, element);
    return newElement;
}

/**
 * Add event listener with automatic cleanup tracking
 */
class EventManager {
    private listeners: Map<string, Array<{element: Element, event: string, handler: EventListener}>> = new Map();
    
    addEventListener(componentId: string, element: Element, event: string, handler: EventListener): void {
        element.addEventListener(event, handler);
        
        if (!this.listeners.has(componentId)) {
            this.listeners.set(componentId, []);
        }
        this.listeners.get(componentId)!.push({element, event, handler});
    }
    
    removeComponentListeners(componentId: string): void {
        const componentListeners = this.listeners.get(componentId);
        if (componentListeners) {
            componentListeners.forEach(({element, event, handler}) => {
                element.removeEventListener(event, handler);
            });
            this.listeners.delete(componentId);
        }
    }
    
    removeAll(): void {
        this.listeners.forEach((_, componentId) => {
            this.removeComponentListeners(componentId);
        });
    }
}

export const eventManager = new EventManager();

/**
 * Returns a debounced version of fn that delays execution by `delay` ms.
 */
export function debounce<T extends (...args: any[]) => void>(fn: T, delay: number): T {
    let timer: ReturnType<typeof setTimeout> | null = null;
    return function(this: any, ...args: any[]) {
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), delay);
    } as T;
}
